import React, { Fragment } from 'react';
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Col, Form, FormGroup, Label, Input, Alert, Spinner
} from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReactTable from "react-table";
import {createGroup, getAuthenticatedDomains} from "../../../API/PhishingAPI";
import Papa from 'papaparse';
import { unauthenticatedRecipients } from '../../../shared/authenticatedDomains';
import { DeliverySettingsPanel } from '../DeliverySettings/DeliverySettings';
import DemoCSVTemplate from '../group_template.csv';
import { authenticate, listDirectoryAccounts } from 'API/OutlookAPI';

export function mergeDirectoryAccounts(targets, accounts) {
  const merged = [...targets];
  const emails = new Set(targets.map(target => (target.email || '').trim().toLowerCase()));
  for (const account of accounts) {
    if (!account || typeof account.email !== 'string') continue;
    const email = account.email.trim();
    if (!/^[^\s@]+@[^\s@]+$/.test(email) || emails.has(email.toLowerCase())) continue;
    const names = typeof account.name === 'string' ? account.name.trim().split(/\s+/) : [];
    merged.push({ first_name: names[0] || '', last_name: names.slice(1).join(' '),
      email, position: account.department || '' });
    emails.add(email.toLowerCase());
  }
  return merged;
}

export class UsersAndGroupsModal extends React.Component{
  constructor(props){
    super(props);
    this.state = {
      modal: this.props.open,
      isEdit: this.props.group ? true:false,
      group:{
        id:"",
        name:"",
        targets:[]
      },
      target:{},
      targets:[],
      importingAD: false,
      adNeedsConsent: false,
      adError: '',
      adMessage: '',
      saving: false,
      removedIndices: [],
      errorsInUserInput:{},
      errors:{},
      domainPolicy: { loading: true, error: false, domains: [] },
      importError: ''
    }
    this.toggle = this.toggle.bind(this);
    this.handleChangeForUser = this.handleChangeForUser.bind(this);
    this.submitUser = this.submitUser.bind(this);
    this.buildUserInfo = this.buildUserInfo.bind(this);
    this.uploadBulkUsers = this.uploadBulkUsers.bind(this);
    this.importADUsers = this.importADUsers.bind(this);
    this.submitGroup = this.submitGroup.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleValidationForUser = this.handleValidationForUser.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
    this.removeUser = this.removeUser.bind(this);
  }
  
  componentDidMount(){
    this.mounted = true;
    if (this.state.modal) this.loadDomainPolicy();
    if(this.state.isEdit){
      this.setState({
        group: {...this.props.group},
        targets: [...this.props.group.targets]
      })
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.adRequest = null;
    this.domainRequest = null;
    this.csvRequest = null;
  }

  loadDomainPolicy = async () => {
    const request = {}; this.domainRequest = request;
    this.setState(current => ({ domainPolicy: { ...current.domainPolicy, loading: true, error: false } }));
    try {
      const result = await getAuthenticatedDomains();
      if (!Array.isArray(result?.domains)) throw new Error('Domain registry unavailable');
      if (this.domainRequest === request) this.setState({ domainPolicy: { loading: false, error: false, domains: result.domains } });
    } catch (_) {
      if (this.domainRequest === request) this.setState({ domainPolicy: { loading: false, error: true, domains: [] } });
    }
  };
  closeDelivery = () => { this.setState({ deliveryOpen: false }); this.loadDomainPolicy(); };

  buildUserInfo(firstName, lastName, email, position) {
    const userInfo = {};
    userInfo["first_name"] = firstName;
    userInfo["last_name"] = lastName;
    userInfo["email"] = email;
    userInfo["position"] = position;
    return userInfo;
  }

  uploadBulkUsers(event) {
    const files = event.target.files;
    const csvFile= files[0];
    const reader = new FileReader();
    const request = {}; this.csvRequest = request; this.setState({ importError: '' });
    reader.onload = (e) => {
      if (!this.mounted || this.csvRequest !== request) return;
      const fileText = e.target.result;
      const parsed = Papa.parse(fileText, { skipEmptyLines: 'greedy' });
      if (parsed.errors.length) { this.setState({ importError: this.props.t('authenticated_domains.invalid_csv') }); return; }
      const allTextLines = parsed.data;
      const userList = [];
      for (var i = 1; i < allTextLines.length; i++) {
        const rawUserInfo = allTextLines[i];
        const userInfo = this.buildUserInfo(
          rawUserInfo[0] || "",
          rawUserInfo[1] || "",
          rawUserInfo[2] || "",
          rawUserInfo[3] || "");
        userList.push(userInfo);
      }
      this.setState(previousState => ({
        targets:[...previousState.targets, ...userList]
      }));
    };
    reader.onerror = () => { if (this.mounted && this.csvRequest === request) this.setState({ importError: this.props.t('authenticated_domains.invalid_csv') }); };
    if (csvFile) {
      reader.readAsText(csvFile);
    }
  }

  async importADUsers(authorize = false) {
    if (this.adRequest) return;
    const request = {};
    this.adRequest = request;
    this.setState({ importingAD: true, adError: '', adMessage: '' });
    try {
      if (authorize) await authenticate();
      if (this.adRequest !== request) return;
      const accounts = await listDirectoryAccounts();
      if (this.adRequest !== request) return;
      this.setState(previousState => {
        const targets = mergeDirectoryAccounts(previousState.targets, accounts);
        const count = targets.length - previousState.targets.length;
        return { targets, adNeedsConsent: false,
          adMessage: this.props.t(count ? 'users_and_groups.messages.ad_imported' : 'users_and_groups.messages.ad_no_new_users', { count }) };
      });
    } catch (error) {
      if (this.adRequest !== request) return;
      const needsConsent = [401, 403].includes(Number(error.code)) ||
        error.errorCode === 'consent_required' || error.subError === 'consent_required';
      let message = this.props.t('users_and_groups.messages.ad_failed', { message: error.message || '' });
      if (needsConsent) message = this.props.t('users_and_groups.messages.ad_consent');
      else if (['popup_window_error', 'empty_window_error'].includes(error.errorCode)) {
        message = this.props.t('organizations.messages.error-blocking-popup');
      } else if (error.errorCode === 'user_cancelled') {
        message = this.props.t('users_and_groups.messages.ad_cancelled');
      }
      this.setState({ adError: message, adNeedsConsent: needsConsent || authorize });
    } finally {
      if (this.adRequest === request) {
        this.adRequest = null;
        this.setState({ importingAD: false });
      }
    }
  }

  handleChange(event){
    const key = event.target.name;
    const value = event.target.type === 'checkbox'
      ? event.target.checked : event.target.value;
    this.setState(updater => ({
      group: {
        ...updater.group,
        [key]: value
      }
    }));
  }

  handleChangeForUser(event){
    const key = event.target.name;
    const value = event.target.type === 'checkbox'
      ? event.target.checked : event.target.value;
    this.setState(updater => ({
      target: {
        ...updater.target,
        [key]: value
      }
    }));
  }

  handleValidationForUser(){
    var fields = this.state.target;
    var errors = {};
    var formIsValid = true;
    //First Name
    if(!fields["first_name"]){
      formIsValid = false;
      errors["first_name"] = "Cannot be empty";
    }
    //Last Name
    if(!fields["last_name"]){
      formIsValid = false;
      errors["last_name"] = "Cannot be empty";
    }
    //Email
    if(!fields["email"]){
      formIsValid = false;
      errors["email"] = "Cannot be empty";
    }
    if(typeof fields["email"] !== "undefined"){
      let lastAtPos = fields["email"].lastIndexOf('@');
      let lastDotPos = fields["email"].lastIndexOf('.');
      if (!(lastAtPos < lastDotPos && lastAtPos > 0 && fields["email"].indexOf('@@') == -1
      && lastDotPos > 2 && (fields["email"].length - lastDotPos) > 2)) {
        formIsValid = false;
        errors["email"] = "Email is not valid";
      }
    }
    this.setState({errorsInUserInput: errors});
    return formIsValid;
  }

  handleValidation(){
    var fields = this.state.group;
    var errors = {};
    var formIsValid = true;
    if(!fields["name"]){
      formIsValid = false;
      errors["name"] = "Group Name cannot be empty";
    }
    this.setState({errors: errors});
    return formIsValid;
  }

  submitUser(){
    if(!this.handleValidationForUser()){
      return;
    }
    if (this.state.domainPolicy.loading || this.state.domainPolicy.error || unauthenticatedRecipients([this.state.target], this.state.domainPolicy.domains).length) {
      this.setState({ errorsInUserInput: { email: this.props.t('authenticated_domains.manual_blocked') } }); return;
    }
    this.setState(previousState => ({
      targets:[...previousState.targets, this.state.target]
      /*group:{
        ...previousState.group,
        targets:[...previousState.group.targets, this.state.target]
      }*/,
      target:{
        first_name:"",
        last_name:"",
        email:"",
        position:""
      },
      errors:{}
    }));
  }

  submitGroup(){
    if (this.saving || this.state.importingAD) return;
    if (this.state.domainPolicy.loading || this.state.domainPolicy.error || unauthenticatedRecipients(this.state.targets, this.state.domainPolicy.domains).length) return;
    if(!this.handleValidation()){
      return;
    }
    if(this.state.targets.length === 0){
      toast["error"]("No target specified");
      return
    }
    const group = { ...this.state.group, targets: [...this.state.targets] };
    this.saving = true;
    this.setState({ saving: true });
    const promise = createGroup(group);
    promise
      .then(result => {
        if (!this.mounted) return;
        this.saving = false;
        this.toggle();
        this.props.onSubmit({ ...group, ...result });
      }).catch(error => {
        if (!this.mounted) return;
        const messageKey = this.state.isEdit ? 'errors.updating' : 'errors.creating';
        toast["error"](this.props.t(messageKey, { element: 'Group', message: error.message }));
      }).finally(() => {
        this.saving = false;
        if (this.mounted) this.setState({ saving: false });
      });
  }

  removeUser(row){
    let targets = this.state.targets.filter((ele, index) => index != row._index);
    this.setState({
      targets: targets
    })
  }

  toggle(){
    if (this.saving && this.state.saving) return;
    this.adRequest = null;
    this.domainRequest = null; this.csvRequest = null;
    const closing = this.state.modal;
    this.setState({
      modal: !this.state.modal,
      group:{
        name:"",
        targets:[]
      },
      target:{},
      targets:[],
      importingAD: false,
      adNeedsConsent: false,
      adError: '',
      adMessage: '',
      saving: false,
      errors:{},
      errorsInUserInput:{}, importError: ''
    }, () => { if (!closing) this.loadDomainPolicy(); });
    if (closing && typeof this.props.onClose === 'function') this.props.onClose();
  }

  render(){
    const { t } = this.props;
    const blocked = unauthenticatedRecipients(this.state.targets, this.state.domainPolicy.domains);
    const blockedDomains = [...new Set(blocked.map(target => target.domain || t('authenticated_domains.invalid_email')))].join(', ');

    const openButton = this.props.showOpenButton &&
    <Button className="mb-2 mr-2 btn-icon btn-shadow" size="lg" color="success" onClick={() => this.toggle()}>
      <i className="lnr-plus-circle btn-icon-wrapper"> </i>
      {t('users_and_groups.actions.create')}
    </Button>;

    return(
      <Fragment>
        {openButton}
        <Modal isOpen={this.state.modal} toggle={this.toggle} size="lg">
          <ModalHeader toggle={this.state.saving ? undefined : this.toggle}>
            {t(this.state.isEdit ? 'users_and_groups.actions.edit' : 'users_and_groups.actions.create')}
          </ModalHeader>
          <ModalBody>
            <fieldset className="border-0 p-0 m-0" disabled={this.state.saving}>
            {this.props.contextHelp && <div className="alert alert-info" role="note">{this.props.contextHelp}</div>}
            <FormGroup>
              <Label>{t('users_and_groups.name')}:</Label>
              <Input type="text" name="name" id="name" placeholder="Group Name" value={this.state.group.name} onChange={this.handleChange}/>
              <span style={{color: "red"}}>{this.state.errors["name"]}</span>
            </FormGroup>
            <FormGroup row>
              <Col sm={6}>
                <Input id="attachmentUpload" type="file" hidden accept=".csv" onChange={this.uploadBulkUsers} />
                <Button className="mb-2 mr-2 btn-icon btn-shadow" size="md" color="primary" onClick={() => document.getElementById("attachmentUpload").click()}>
                  <i className="lnr-plus-circle btn-icon-wrapper"> </i>
                  {t('users_and_groups.actions.import_users')}
                </Button>
                <a id="downloadTemplate" href={DemoCSVTemplate} download="" hidden>_</a>
                <Button color="link" className='mb-2' onClick={() => document.getElementById("downloadTemplate").click()}>
                  {t('users_and_groups.actions.download_template')}
                </Button>
              </Col>
              <Col sm={6}>
                <Button className="mb-2 mr-2 btn-icon btn-shadow" size="md" color="primary"
                  disabled={this.state.importingAD} aria-busy={this.state.importingAD}
                  onClick={() => this.importADUsers(this.state.adNeedsConsent)}>
                  <i className="lnr-cloud-sync btn-icon-wrapper"> </i>
                  {t(this.state.importingAD ? 'users_and_groups.actions.importing_ad' :
                    this.state.adNeedsConsent ? 'users_and_groups.actions.authorize_ad' : 'users_and_groups.actions.import_from_ad')}
                </Button>
              </Col>
            </FormGroup>
            {this.state.adError && <Alert color="danger" role="alert">{this.state.adError}</Alert>}
            {this.state.adMessage && <Alert color="info" role="status">{this.state.adMessage}</Alert>}
            {this.state.importError && <Alert color="danger" role="alert">{this.state.importError}</Alert>}
            <div className="recipient-domain-policy">
              {this.state.domainPolicy.loading ? <p role="status"><Spinner tag="span" size="sm" /> {t('authenticated_domains.validating')}</p>
                : this.state.domainPolicy.error ? <Alert color="danger" role="alert">{t('authenticated_domains.policy_error')}{' '}
                  <Button color="link" onClick={this.loadDomainPolicy}>{t('template_gallery.retry')}</Button></Alert>
                  : blocked.length > 0 ? <Alert color="danger" role="alert">{t('authenticated_domains.blocked', { domains: blockedDomains })}</Alert>
                    : <p className="text-muted">{t('authenticated_domains.import_help')}</p>}
              <Button color="link" className="p-0 mb-3" onClick={() => this.setState({ deliveryOpen: true })}>{t('authenticated_domains.manage')}</Button>
            </div>
            <Form onSubmit={(e) => { e.preventDefault(); this.submitUser(); }}>
              <FormGroup row>
                  <Col sm={2}>
                    <Input type="text" name="first_name" id="first_name" placeholder={t('users_and_groups.first_name')}
                    value={this.state.target.first_name || ''} onChange={this.handleChangeForUser}/>
                    <span style={{color: "red"}}>{this.state.errorsInUserInput["first_name"]}</span>
                  </Col>
                  <Col sm={2}>
                    <Input type="text" name="last_name" id="last_name" placeholder={t('users_and_groups.last_name')}
                    value={this.state.target.last_name || ''} onChange={this.handleChangeForUser}/>
                    <span style={{color: "red"}}>{this.state.errorsInUserInput["last_name"]}</span>
                  </Col>
                  <Col sm={3}>
                    <Input type="email" name="email" id="email" placeholder={t('users_and_groups.email')}
                    value={this.state.target.email || ''} onChange={this.handleChangeForUser}/>
                    <span style={{color: "red"}}>{this.state.errorsInUserInput["email"]}</span>
                  </Col>
                  <Col sm={3}>
                    <Input type="text" name="position" id="position" placeholder={t('users_and_groups.department')}
                    value={this.state.target.position || ''} onChange={this.handleChangeForUser}/>
                  </Col>
                  <Col sm={2}>
                    <Button className="mb-2 mr-2 btn-icon btn-shadow" size="md" color="primary" onClick={this.submitUser}>
                      <i className="lnr-plus-circle btn-icon-wrapper"> </i>
                      {t('users_and_groups.actions.add')}
                    </Button>
                  </Col>
              </FormGroup>
            </Form>
            <FormGroup>
            {this.state.targets.length > 0 && <ReactTable
              columns={[
                {
                  Header: t("users_and_groups.user_name_list.first_name"),
                  accessor : 'first_name'
                },
                {
                  Header: t("users_and_groups.user_name_list.last_name"),
                  accessor : 'last_name'
                },
                {
                  Header: t("users_and_groups.user_name_list.email"),
                  accessor : 'email',
                  Cell: ({ value }) => <span className={unauthenticatedRecipients([{ email: value }], this.state.domainPolicy.domains).length ? 'text-danger' : undefined}>{value}</span>
                },
                {
                  Header: t("users_and_groups.user_name_list.department"),
                  accessor : 'position'
                },
                {
                  Header: "Actions",
                  Cell: ({row}) => (
                    <div className="d-block w-100 text-center">
                      <Button className="mb-2 mr-2 btn-icon btn-shadow" size="md" color="success" onClick={() => {this.removeUser(row);} }>
                        <i className="pe-7s-trash btn-icon-wrapper mx-auto"> </i>
                      </Button>
                    </div>
                  )
                }
              ]}
                data={this.state.targets}
                showPageSizeOptions={false}
                defaultPageSize={10}
                pageSize={this.state.targets.length > 10 ? 10 : this.state.targets.length}
                className="-striped -highlight card"
                />
              }
            </FormGroup>
            </fieldset>
          </ModalBody>
          <ModalFooter>
            <Button color="link" disabled={this.state.saving} onClick={this.toggle}>{t('users_and_groups.actions.cancel')}</Button>
            <Button color="primary" disabled={this.state.importingAD || this.state.saving || this.state.domainPolicy.loading || this.state.domainPolicy.error || blocked.length > 0} onClick={this.submitGroup}>
              {this.state.saving ? t('template_gallery.saving') : this.props.submitLabel || t('users_and_groups.actions.save')}
            </Button>{' '}
          </ModalFooter>
        </Modal>
        {this.state.deliveryOpen && <Modal isOpen toggle={this.closeDelivery} size="xl" scrollable className="delivery-settings-dialog">
          <ModalHeader toggle={this.closeDelivery}>{t('delivery_settings.title')}</ModalHeader>
          <ModalBody><DeliverySettingsPanel t={t} /></ModalBody>
          <ModalFooter><Button color="link" onClick={this.closeDelivery}>{t('template_gallery.cancel')}</Button></ModalFooter>
        </Modal>}

      </Fragment>
    )
  }
}

UsersAndGroupsModal.defaultProps = {
  open: false,
  showOpenButton: true
}

export default withTranslation()(UsersAndGroupsModal);
